import React, { useCallback, useEffect } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import type { RouteProp } from '@react-navigation/native';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  BodyText,
  Button,
  Modal,
  ProductCard,
  theme,
  TransparentModal,
  useResponsive,
} from '@sg/garnish';

import { LoadingAnimation, NotFoundView } from '@order/components';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import type { ModalStackParamList } from '../../../../navigation/AppNavigation.props';
import { OrderProductGuard } from '../../components/OrderProductGuard';
import { useOrderProductConfirmProductScreenData } from './hooks/useOrderProductConfirmProductScreenData';

export const OrderProductConfirmProductScreen = () => {
  const { t } = useLocalizationContext();

  const { params } =
    useRoute<RouteProp<ModalStackParamList, 'OrderProductConfirmProduct'>>();
  const { productSlug } = params ?? {};

  const { baseProduct, isFetchingBaseProduct } =
    useOrderProductConfirmProductScreenData({
      productSlug,
    });

  if (isFetchingBaseProduct)
    return (
      <OrderProductConfirmProductContainer>
        <Modal.Row style={styles.notLoadedViewContainer}>
          <LoadingAnimation size="large" />
        </Modal.Row>
      </OrderProductConfirmProductContainer>
    );

  if (!baseProduct || !baseProduct.isDeepLinkingEnabled)
    return (
      <OrderProductConfirmProductContainer>
        <Modal.Row style={styles.notLoadedViewContainer}>
          <NotFoundView
            withoutFooter
            description={
              baseProduct && !baseProduct.isDeepLinkingEnabled
                ? t('order-product.not-available')
                : undefined
            }
          />
        </Modal.Row>
      </OrderProductConfirmProductContainer>
    );

  return (
    <OrderProductConfirmProductContainer>
      <OrderProductConfirmProductLoaded baseProduct={baseProduct} />
    </OrderProductConfirmProductContainer>
  );
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const OrderProductConfirmProductContainer = (
  props: Pick<ViewProps, 'children'>,
) => {
  const navigation = useNavigation();
  const { match, minWidth } = useResponsive();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const dismissModal = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();

      return;
    }

    navigation.getParent()?.goBack();
  }, [navigation]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <OrderProductGuard>
      <TransparentModal
        size={match(['full', 'small'])}
        onClose={dismissModal}
        mobileEdges={['right', 'bottom', 'left']}
      >
        {minWidth.tablet ? (
          <Modal.FloatingCloseBtn onPress={dismissModal} />
        ) : null}

        {props.children}
      </TransparentModal>
    </OrderProductGuard>
  );
};

const OrderProductConfirmProductLoaded = (
  props: OrderProductConfirmProductLoadedProps,
) => {
  const { baseProduct } = props;

  const { params } =
    useRoute<RouteProp<ModalStackParamList, 'OrderProductConfirmProduct'>>();
  const { productSlug } = params ?? {};

  const navigation = useNavigation();
  const { t } = useLocalizationContext();
  const { track } = useTelemetry();
  const { match, currentBreakpoint, minWidth } = useResponsive();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleContinuePress = useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'OrderProductConfirmLocation',
      params: { productSlug },
    });
  }, [navigation, productSlug]);

  const descriptionSize = currentBreakpoint.isMobile ? 4 : 3;
  const productCardContainerStyles = [
    styles.productCardContainer,
    match([styles.productCardContainerXS, styles.productCardContainerSM]),
  ];

  useEffect(() => {
    track('deep-linking.start');
  }, [track]);

  return (
    <>
      {baseProduct.heading ? (
        <Modal.Headline
          size={match([1, 2, 3])}
          style={match([undefined, styles.headerSM])}
        >
          {baseProduct.heading}
        </Modal.Headline>
      ) : null}

      <Modal.Row style={styles.contentContainer}>
        {baseProduct.subheading ? (
          <BodyText size={descriptionSize}>{baseProduct.subheading}</BodyText>
        ) : null}

        <View style={productCardContainerStyles}>
          <ProductCard
            name={baseProduct.name}
            description={baseProduct.description}
            label={baseProduct.isSeasonal ? t('general.seasonal') : ''}
            cloudinaryImageSrc={baseProduct?.asset?.url}
          />
        </View>
      </Modal.Row>

      <Modal.Footer
        style={match([styles.footerXS, styles.footerSM])}
        withoutTopBorder={minWidth.tablet}
      >
        <Button
          size="large"
          testID="order-product.confirm-product-continue"
          onPress={handleContinuePress}
          accessibilityLabel={t('order-product.continue')}
          accessibilityHint={t('order-product.continue')}
        >
          {t('order-product.continue')}
        </Button>
      </Modal.Footer>
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  headerSM: {
    marginTop: theme.spacing.small,
    paddingTop: theme.spacing.xlarge,
  },
  contentContainer: {
    flex: 1,
  },
  notLoadedViewContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: theme.spacing.large,
  },
  productCardContainer: {
    paddingTop: theme.spacing.large,
    width: '100%',
    alignSelf: 'center',
  },
  productCardContainerXS: {
    maxWidth: 400,
  },
  productCardContainerSM: {
    maxWidth: 342,
  },
  footerXS: {
    paddingVertical: theme.spacing.medium,
  },
  footerSM: {
    paddingTop: theme.spacing.large,
    paddingHorizontal: theme.spacing.xlarge,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderProductConfirmProductLoadedProps = {
  readonly baseProduct: Exclude<
    ReturnType<typeof useOrderProductConfirmProductScreenData>['baseProduct'],
    null
  >;
};
